import { GetReportCategoriesResponse, GetReportsResponse } from 'src/types/apiResponse';
import { Report } from 'src/types/report';
import { ReportCategory } from 'src/types/reportCategory';
import { FrequencyType } from 'src/types/frequencyType';

// Flag to enable/disable mock data - set to false to use real API
export const USE_MOCK_REPORTS_DATA = false;

// Helper to generate timestamp from date string (in SECONDS, not milliseconds)
const toTimestamp = (dateStr: string): number => Math.floor(new Date(dateStr).getTime() / 1000);

// Mock Frequency Types
export const mockFrequencyTypes: FrequencyType[] = [
    { code: 'DLY', name: 'Daily' },
    { code: 'WLY', name: 'Weekly' },
    { code: 'MLY', name: 'Monthly' }
];

// Mock Report Categories — sized to mirror what KHL has in QA so the "All"
// view picks the latest per category and individual category views show
// multiple historical reports.
export const mockReportCategories: ReportCategory[] = [
    {
        code: 'ARRF',
        name: 'AR Roll Forward',
        frequencyTypes: [mockFrequencyTypes[0], mockFrequencyTypes[2]],
        partners: [],
        externalLink: '',
        ownerEmails: []
    },
    {
        code: 'COR',
        name: 'Charge Off Report',
        frequencyTypes: [mockFrequencyTypes[2]],
        partners: [],
        externalLink: '',
        ownerEmails: []
    },
    {
        code: 'COPR',
        name: 'Operational Report',
        frequencyTypes: [mockFrequencyTypes[2]],
        partners: [],
        externalLink: '',
        ownerEmails: []
    },
    {
        code: 'RCV',
        name: 'Recoveries',
        frequencyTypes: [mockFrequencyTypes[0], mockFrequencyTypes[2]],
        partners: [],
        externalLink: '',
        ownerEmails: []
    },
    {
        code: 'KPIS',
        name: 'KPI Summary',
        frequencyTypes: [mockFrequencyTypes[2]],
        partners: [],
        externalLink: '',
        ownerEmails: []
    },
    {
        code: 'DIAL',
        name: 'Collections Pen Rate',
        frequencyTypes: [mockFrequencyTypes[0]],
        partners: [],
        externalLink: '',
        ownerEmails: []
    },
    {
        code: 'DELQ',
        name: 'Delinquency Report',
        frequencyTypes: [mockFrequencyTypes[2]],
        partners: [],
        externalLink: '',
        ownerEmails: []
    },
    {
        code: 'STTL',
        name: 'Settlement',
        frequencyTypes: [mockFrequencyTypes[2]],
        partners: [],
        externalLink: '',
        ownerEmails: []
    },
    {
        code: 'PORT',
        name: 'Full Portfolio',
        frequencyTypes: [mockFrequencyTypes[2]],
        partners: [],
        externalLink: '',
        ownerEmails: []
    }
];

// Helper: build a Report fixture for KHL in a given category/frequency.
const makeReport = (
    categoryCode: string,
    frequency: 'DLY' | 'WLY' | 'MLY',
    fileName: string,
    reportDate: string,
    lastModified: string,
    fileSize: string,
    rawSize: number,
    pathSuffix?: string
): Report => {
    const datePath = reportDate.split('-').join('/'); // 2026-06-22 → 2026/06/22
    const key = `Reports/EXT/KHL/${categoryCode}/${datePath}/${frequency}/${pathSuffix || fileName}`;
    return {
        id: btoa(key),
        bucketName: 'cof-card-pships-qa-copp-us-east-1',
        fileName,
        key,
        lastModified: toTimestamp(`${lastModified}T08:00:00Z`),
        owner: 'system',
        reportDate: toTimestamp(reportDate),
        fileSize,
        rawSize,
    };
};

// ===== KHL Mock Reports =====
// Mirrors the data the user pasted from QA, with a few historical entries per
// category so each one renders multiple reports.

// Collections Pen Rate (DIAL) — 20+ files dated 2026-06-22, plus a few from
// 2026-06-21 so the date filter has history to walk back through.
const dialPenRateTimes06_22 = [
    { time: '0150AM', size: '7 KB', raw: 7168 },
    { time: '0150PM', size: '8 KB', raw: 8192 },
    { time: '0250AM', size: '7 KB', raw: 7168 },
    { time: '0250PM', size: '8 KB', raw: 8192 },
    { time: '0350AM', size: '7 KB', raw: 7168 },
    { time: '0350PM', size: '8 KB', raw: 8192 },
    { time: '0450AM', size: '7 KB', raw: 7168 },
    { time: '0450PM', size: '8 KB', raw: 8192 },
    { time: '0550AM', size: '7 KB', raw: 7168 },
    { time: '0550PM', size: '8 KB', raw: 8192 },
    { time: '0650AM', size: '7 KB', raw: 7168 },
    { time: '0650PM', size: '8 KB', raw: 8192 },
    { time: '0750AM', size: '7 KB', raw: 7168 },
    { time: '0750PM', size: '8 KB', raw: 8192 },
    { time: '0850AM', size: '8 KB', raw: 8192 },
    { time: '0950AM', size: '8 KB', raw: 8192 },
    { time: '1050AM', size: '8 KB', raw: 8192 },
    { time: '1150AM', size: '8 KB', raw: 8192 },
    { time: '1250AM', size: '7 KB', raw: 7168 },
    { time: '1250PM', size: '8 KB', raw: 8192 },
];

const dialReports06_22: Report[] = dialPenRateTimes06_22.map(({ time, size, raw }) =>
    makeReport(
        'DIAL',
        'DLY',
        `Kohls_Collections_Pen_Rate_${time}.xlsx`,
        '2026-06-22',
        time === '0750PM' ? '2026-06-23' : '2026-06-22',
        size,
        raw
    )
);

const dialReports06_21: Report[] = ['0150AM', '0750AM', '1250PM'].map((time) =>
    makeReport(
        'DIAL',
        'DLY',
        `Kohls_Collections_Pen_Rate_${time}_20260621.xlsx`,
        '2026-06-21',
        '2026-06-21',
        '8 KB',
        8192
    )
);

export const mockReports: Report[] = [
    // ===== Recoveries (RCV) =====
    makeReport('RCV', 'DLY', 'Kohls_Daily_Recoveries.xlsx',           '2026-06-22', '2026-06-22', '34 KB',  34816),
    makeReport('RCV', 'DLY', 'Kohls_Daily_Recoveries_20260621.xlsx',  '2026-06-21', '2026-06-21', '33 KB',  33792),
    makeReport('RCV', 'DLY', 'Kohls_Daily_Recoveries_20260620.xlsx',  '2026-06-20', '2026-06-20', '34 KB',  34816),
    makeReport('RCV', 'MLY', 'Consumer_Kohls_recoveries_Report_20260531.xlsx', '2026-05-31', '2026-06-10', '125 MB', 131072000),
    makeReport('RCV', 'MLY', 'Consumer_Kohls_recoveries_Report_20260430.xlsx', '2026-04-30', '2026-05-08', '118 MB', 123731968),

    // ===== Collections Pen Rate (DIAL) =====
    ...dialReports06_22,
    ...dialReports06_21,

    // ===== AR Roll Forward (ARRF) =====
    makeReport('ARRF', 'DLY', 'AR_Roll_Forward_20260620.xls', '2026-06-20', '2026-06-21', '152 KB', 155648),
    makeReport('ARRF', 'DLY', 'AR_Roll_Forward_20260619.xls', '2026-06-19', '2026-06-20', '149 KB', 152576),
    makeReport('ARRF', 'DLY', 'AR_Roll_Forward_20260618.xls', '2026-06-18', '2026-06-19', '151 KB', 154624),
    makeReport('ARRF', 'DLY', 'AR_Roll_Forward_20260617.xls', '2026-06-17', '2026-06-18', '148 KB', 151552),

    // ===== Full Portfolio (PORT) =====
    makeReport('PORT', 'MLY', 'KohlsFullPorfolio.xls',          '2026-06-08', '2026-06-08', '23 MB', 24117248),
    makeReport('PORT', 'MLY', 'KohlsFullPorfolio_20260508.xls', '2026-05-08', '2026-05-08', '22 MB', 23068672),
    makeReport('PORT', 'MLY', 'KohlsFullPorfolio_20260408.xls', '2026-04-08', '2026-04-08', '21 MB', 22020096),

    // ===== Charge Off Report (COR) =====
    makeReport('COR', 'MLY', 'Consumer_Kohls_ChargeOff_Report_20260531.xlsx', '2026-05-31', '2026-06-06', '2 MB', 2097152),
    makeReport('COR', 'MLY', 'Consumer_Kohls_ChargeOff_Report_20260430.xlsx', '2026-04-30', '2026-05-06', '1.9 MB', 1992295),
    makeReport('COR', 'MLY', 'Consumer_Kohls_ChargeOff_Report_20260331.xlsx', '2026-03-31', '2026-04-06', '2 MB', 2097152),

    // ===== Operational Report (COPR) =====
    makeReport('COPR', 'MLY', 'Consumer_Kohls_Operational_Report_20260531.xlsx', '2026-05-31', '2026-06-18', '257 KB', 263168),
    makeReport('COPR', 'MLY', 'Consumer_Kohls_Operational_Report_20260430.xlsx', '2026-04-30', '2026-05-18', '249 KB', 254976),
    makeReport('COPR', 'MLY', 'Consumer_Kohls_Operational_Report_20260331.xlsx', '2026-03-31', '2026-04-18', '252 KB', 258048),

    // ===== Delinquency Report (DELQ) =====
    makeReport('DELQ', 'MLY', 'Delinquency_Monthly_Report_KOHLS_Report_May2026.xlsx',   '2026-05-31', '2026-06-07', '107 KB', 109568),
    makeReport('DELQ', 'MLY', 'Delinquency_Monthly_Report_KOHLS_Report_April2026.xlsx', '2026-04-30', '2026-05-07', '104 KB', 106496),
    makeReport('DELQ', 'MLY', 'Delinquency_Monthly_Report_KOHLS_Report_March2026.xlsx', '2026-03-31', '2026-04-07', '109 KB', 111616),

    // ===== KPI Summary (KPIS) =====
    makeReport('KPIS', 'MLY', 'Consumer_kohls_KPI_Summary_kohls.xlsx',          '2026-05-31', '2026-06-18', '73 KB', 74752),
    makeReport('KPIS', 'MLY', 'Consumer_kohls_KPI_Summary_kohls_20260430.xlsx', '2026-04-30', '2026-05-18', '71 KB', 72704),
    makeReport('KPIS', 'MLY', 'Consumer_kohls_KPI_Summary_kohls_20260331.xlsx', '2026-03-31', '2026-04-18', '74 KB', 75776),

    // ===== Settlement (STTL) =====
    makeReport('STTL', 'MLY', '10_October_Kohls_TSYS Settlement_Distro.xlsx',    '2025-11-10', '2025-11-10', '2 MB', 2097152),
    makeReport('STTL', 'MLY', '09_September_Kohls_TSYS Settlement_Distro.xlsx', '2025-10-10', '2025-10-10', '2 MB', 2097152),
    makeReport('STTL', 'MLY', '08_August_Kohls_TSYS Settlement_Distro.xlsx',    '2025-09-10', '2025-09-10', '1.9 MB', 1992295),
];

// Get mock report categories response
export const getMockReportCategoriesResponse = (partnerCode: string, accessType: string): GetReportCategoriesResponse => ({
    categories: mockReportCategories,
    selectedPartner: partnerCode,
    selectedAccessType: accessType
});

// Get mock reports response for a specific category
export const getMockReportsResponse = (
    categoryCode: string,
    partnerCode: string,
    accessType: string
): GetReportsResponse => {
    // Filter reports by category (based on key path)
    const categoryReports = mockReports.filter(r => r.key.includes(`/${categoryCode}/`));

    // Group by frequency type
    const reportsByFrequency: { [key: string]: Report[] } = {};
    categoryReports.forEach(report => {
        const freqCode = report.key.includes('/DLY/') ? 'DLY' : report.key.includes('/WLY/') ? 'WLY' : 'MLY';
        if (!reportsByFrequency[freqCode]) {
            reportsByFrequency[freqCode] = [];
        }
        reportsByFrequency[freqCode].push(report);
    });

    // Find latest and oldest reports
    const sortedReports = [...categoryReports].sort((a, b) => b.reportDate - a.reportDate);
    const latestReport = sortedReports[0];
    const oldestReport = sortedReports[sortedReports.length - 1];

    return {
        frequencyTypes: mockFrequencyTypes,
        reports: reportsByFrequency,
        latestReport: latestReport ? {
            frequencyType: latestReport.key.includes('/DLY/') ? 'DLY' : latestReport.key.includes('/WLY/') ? 'WLY' : 'MLY',
            reportDate: latestReport.reportDate
        } : { frequencyType: 'DLY', reportDate: Date.now() },
        oldestReport: oldestReport ? {
            frequencyType: oldestReport.key.includes('/DLY/') ? 'DLY' : oldestReport.key.includes('/WLY/') ? 'WLY' : 'MLY',
            reportDate: oldestReport.reportDate
        } : { frequencyType: 'DLY', reportDate: Date.now() },
        selectedAccessType: accessType,
        selectedPartner: partnerCode,
        selectedCategory: categoryCode
    };
};
